import React from 'react';
import { motion } from 'framer-motion';
import { Download, Github, Linkedin, Briefcase, GraduationCap, Code2, Calendar, MapPin } from 'lucide-react';

function CVPage({ darkMode, setActivePage }) {
  const themeClasses = {
    background: darkMode ? 'bg-gray-900' : 'bg-gray-50',
    card: darkMode ? 'bg-gray-800' : 'bg-white',
    sectionHeading: `text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} tracking-tight`,
    text: darkMode ? 'text-gray-100' : 'text-gray-800',
    subtext: darkMode ? 'text-gray-400' : 'text-gray-600',
    accent: darkMode ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700' : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600',
    shadow: 'shadow-xl hover:shadow-2xl transition-all duration-300'
  };

  const socialLinks = [
    { icon: <Github size={22} />, link: "https://github.com/berkeakturk1", label: "GitHub" },
    { icon: <Linkedin size={22} />, link: "https://www.linkedin.com/in/berkeakturk/", label: "LinkedIn" },
  ];

  const experienceData = [
    {
      company: 'Taleworlds Entertainment',
      positions: [
        {
          title: 'Software Engineer',
          duration: 'Oct 2025 – Present',
          description: 'Created a workforce management system using React, Node.js, and PostgreSQL, improving HR and management workflows.',
        },
        {
          title: 'Software Engineering Intern',
          duration: 'Jul 2025 – Oct 2025',
          description: 'Created a workforce management system using React, Node.js, and PostgreSQL, improving HR and management workflows.',
        }
      ],
      technologies: "PostgreSQL / React / Node.js"
    },
    {
      company: 'Inavitas',
      positions: [
        {
          title: 'Software Engineering Intern',
          duration: 'Aug 2024 – Sep 2024',
          description: 'Developed a full-stack web application using React, Express, and PostgreSQL. Enabled users to manage taskboards and collaborate on projects.',
        }
      ],
      technologies: "React / Node.js / PostgreSQL"
    },
    {
      company: 'Aselsan Konya',
      positions: [
        {
          title: 'Software Design Intern',
          duration: 'Jul 2023 – Aug 2023',
          description: 'Created a workforce management system using React, Node.js, and PostgreSQL, improving HR and management workflows.',
        }
      ],
      technologies: "PostgreSQL / React / Node.js"
    },
  ];

  const educationData = [
    { degree: 'B.Sc. in Computer Engineering', institution: 'Baskent University', duration: 'Oct 2021 - Jun 2025' },
    { degree: 'AI & Tech Academy', institution: 'Google Academy', duration: 'Oct 2021 - Jun 2025' }
  ];

  const skillsData = ["C#", "C++", "C", "Python", "Java", "Dart", "TypeScript", "React", "Node.js", "Flutter", "Java Spring", "Unity"];

  return (
    <div className={`${themeClasses.background} min-h-screen py-12 px-4 sm:px-6`}>
      <div className="max-w-5xl mx-auto">

        {/* Header Section */}
        <motion.div
          className={`text-center mb-12 p-12 rounded-3xl ${themeClasses.card} shadow-2xl border ${darkMode ? 'border-gray-700/50' : 'border-gray-100'} relative overflow-hidden`}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Gradient Background Effect */}
          <div className="absolute inset-0 opacity-20">
            <div className={`absolute top-0 right-0 w-64 h-64 rounded-full filter blur-3xl ${darkMode ? 'bg-purple-600' : 'bg-indigo-300'} animate-pulse`}></div>
            <div className={`absolute bottom-0 left-0 w-80 h-80 rounded-full filter blur-3xl ${darkMode ? 'bg-indigo-600' : 'bg-purple-300'} animate-pulse`} style={{animationDelay: '1.5s'}}></div>
          </div>

          <div className="relative z-10">
            <div className="h-1.5 w-24 mx-auto mb-6 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full"></div>
            <h1 className={`text-5xl md:text-6xl font-extrabold mb-4 ${themeClasses.text}`}>
              Curriculum Vitae
            </h1>
            <p className={`text-lg ${themeClasses.subtext} max-w-2xl mx-auto mb-8`}>
              Passionate Software Engineer specializing in full-stack development
            </p>

            {/* Social Links */}
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.label}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`px-6 py-3 rounded-xl font-semibold shadow-lg flex items-center gap-2 ${themeClasses.accent} text-white transition-all duration-300 hover:scale-105`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                  whileHover={{ scale: 1.1 }}
                >
                  {social.icon}
                  <span className="text-sm font-semibold">{social.label}</span>
                </motion.a>
              ))}
            </div>

            {/* Download Button */}
            <motion.a
              href="/cv.pdf"
              download="Berke_Akturk_CV.pdf"
              className={`inline-flex items-center gap-3 px-8 py-4 rounded-xl font-semibold shadow-2xl ${themeClasses.accent} text-white transition-all duration-300 hover:scale-110 hover-lift`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.1 }}
            >
              <Download size={20} />
              Download Full CV
            </motion.a>
          </div>
        </motion.div>

        {/* Experience Section */}
        <motion.section
          className="mb-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className={`p-3 rounded-xl ${themeClasses.accent} shadow-lg`}>
              <Briefcase size={28} className="text-white" />
            </div>
            <h2 className={`${themeClasses.sectionHeading} relative`}>
              <span className="relative inline-block">
                Professional Experience
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full"></span>
              </span>
            </h2>
          </div>

          <div className="space-y-6">
            {experienceData.map((exp, index) => (
              <motion.div
                key={index}
                className={`p-8 rounded-2xl ${themeClasses.card} ${themeClasses.shadow} border hover-lift ${darkMode ? 'border-gray-700/50 hover:border-purple-500/50' : 'border-gray-100 hover:border-indigo-200'}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                {/* Company Header */}
                <div className="flex items-center gap-2 mb-6">
                  <MapPin size={18} className={themeClasses.subtext} />
                  <h3 className={`text-2xl font-bold ${themeClasses.text}`}>{exp.company}</h3>
                </div>

                {/* Positions Timeline */}
                <div className="relative pl-8">
                  {/* Vertical Line with Gradient */}
                  {exp.positions.length > 1 && (
                    <div className="absolute left-0 top-3 bottom-3 w-px bg-gradient-to-b from-indigo-500 via-purple-500 to-pink-500"></div>
                  )}

                  {exp.positions.map((position, posIndex) => (
                    <motion.div
                      key={posIndex}
                      className={`relative ${posIndex > 0 ? 'mt-8' : ''}`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 + index * 0.1 + posIndex * 0.15 }}
                    >
                      {/* Timeline Dot with Glow */}
                      {exp.positions.length > 1 && (
                        <div className="absolute -left-8 top-2">
                          <div className={`w-4 h-4 rounded-full border-2 ${
                            posIndex === 0
                              ? darkMode ? 'bg-indigo-500 border-indigo-400 shadow-lg shadow-indigo-500/50' : 'bg-indigo-400 border-indigo-300 shadow-lg shadow-indigo-400/50'
                              : darkMode ? 'bg-purple-500 border-purple-400 shadow-lg shadow-purple-500/50' : 'bg-purple-400 border-purple-300 shadow-lg shadow-purple-400/50'
                          } ring-4 ${darkMode ? 'ring-gray-800' : 'ring-white'}`}></div>
                        </div>
                      )}

                      <div className={`p-5 rounded-xl transition-all duration-300 ${
                        darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'
                      }`}>
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <h4 className={`text-xl font-bold ${themeClasses.text}`}>{position.title}</h4>
                          {posIndex === 0 && exp.positions.length > 1 && (
                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                              darkMode ? 'bg-emerald-900/50 text-emerald-300 border border-emerald-700' : 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                            }`}>
                              Current
                            </span>
                          )}
                        </div>
                        <div className={`flex items-center gap-2 text-sm ${themeClasses.subtext} font-medium mb-3`}>
                          <Calendar size={14} />
                          <span>{position.duration}</span>
                        </div>
                        <p className={`leading-relaxed text-sm ${themeClasses.text}`}>{position.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Technologies */}
                <div className="mt-6 flex flex-wrap gap-2">
                  {exp.technologies.split(' / ').map((tech, i) => (
                    <span
                      key={i}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-lg shadow-sm transition-all duration-300 hover:scale-105 ${
                        darkMode ? 'bg-gradient-to-r from-indigo-900/70 to-purple-900/70 text-indigo-300 border border-indigo-700' : 'bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-800 border border-indigo-200'
                      }`}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Education Section */}
        <motion.section
          className="mb-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className={`p-3 rounded-xl ${themeClasses.accent} shadow-lg`}>
              <GraduationCap size={28} className="text-white" />
            </div>
            <h2 className={`${themeClasses.sectionHeading} relative`}>
              <span className="relative inline-block">
                Education
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full"></span>
              </span>
            </h2>
          </div>

          <div className="space-y-6">
            {educationData.map((edu, index) => (
              <motion.div
                key={index}
                className={`p-8 rounded-2xl ${themeClasses.card} ${themeClasses.shadow} border hover-lift ${darkMode ? 'border-gray-700/50 hover:border-purple-500/50' : 'border-gray-100 hover:border-indigo-200'}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0 + index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <h3 className={`text-2xl font-bold ${themeClasses.text} mb-3`}>{edu.degree}</h3>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <div className={`flex items-center gap-2 ${themeClasses.subtext} font-medium`}>
                    <MapPin size={16} />
                    <span>{edu.institution}</span>
                  </div>
                  <span className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></span>
                  <div className={`flex items-center gap-2 ${themeClasses.subtext} font-medium`}>
                    <Calendar size={16} />
                    <span>{edu.duration}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Skills Section */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className={`p-3 rounded-xl ${themeClasses.accent} shadow-lg`}>
              <Code2 size={28} className="text-white" />
            </div>
            <h2 className={`${themeClasses.sectionHeading} relative`}>
              <span className="relative inline-block">
                Technical Skills
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full"></span>
              </span>
            </h2>
          </div>

          <motion.div
            className={`p-8 rounded-2xl ${themeClasses.card} ${themeClasses.shadow} border ${darkMode ? 'border-gray-700/50' : 'border-gray-100'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3 }}
          >
            <div className="flex flex-wrap gap-3">
              {skillsData.map((skill, index) => (
                <motion.span
                  key={index}
                  className={`px-5 py-2.5 rounded-xl text-sm font-semibold shadow-md transition-all duration-300 hover:scale-110 cursor-default ${
                    darkMode ? 'bg-gradient-to-r from-indigo-900/70 to-purple-900/70 text-indigo-300 border border-indigo-700' : 'bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-800 border border-indigo-200'
                  }`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.4 + index * 0.05 }}
                  whileHover={{ scale: 1.1 }}
                >
                  {skill}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </motion.section>

      </div>
    </div>
  );
}

export default CVPage;
